/**
 * Confines a stylesheet that arrived inside an SVG to one element.
 *
 * An inlined drawing brings its `<style>` with it, and once inlined that is a stylesheet of the
 * page: a file that says `svg { filter: … }` — as one of the design system's illustrations does —
 * restyles every SVG the app renders. Prefixing each selector puts it back inside the box it was
 * drawn for.
 *
 * Two things this walk has to get right, both learned the hard way:
 *
 * - **An at-rule is not one thing.** `@keyframes` holds steps, not selectors, and prefixing `50%`
 *   invalidates the animation while leaving the file looking perfectly fine (everything renders,
 *   nothing moves). But `@media` and its relatives hold ORDINARY RULES, and passing their bodies
 *   through unscoped lets the very leak this module exists to stop walk straight out of a
 *   reduced-motion block.
 * - **Commas are not all top level.** `:is(.a, .b)` and `[data-x="a,b"]` carry commas that do not
 *   separate selectors, and splitting on them produces a selector the engine discards in silence.
 *
 * Comments are stripped before the walk rather than tracked through it: they can hold braces, quotes
 * and commas, they change nothing about how the page renders, and one pass removes the whole class
 * of hazard.
 */

/** At-rules whose body is a list of ordinary rules, so the walk has to go inside. */
const NESTS_RULES = new Set(['@media', '@supports', '@container', '@layer', '@scope']);

function stripComments(css: string): string {
  return css.replace(/\/\*[\s\S]*?\*\//gu, '');
}

/** Index just past the block that opens at `open`, counting braces and ignoring quoted text. */
function blockEnd(css: string, open: number): number {
  let depth = 0;
  let quote = '';
  for (let index = open; index < css.length; index++) {
    const char = css.charAt(index);
    if (quote !== '') {
      if (char === quote && css.charAt(index - 1) !== '\\') {
        quote = '';
      }
    } else if (char === '"' || char === '\'') {
      quote = char;
    } else if (char === '{') {
      depth++;
    } else if (char === '}') {
      depth--;
      if (depth === 0) {
        return index + 1;
      }
    }
  }
  return css.length;
}

/** First `needle` that is not inside quotes or brackets, or -1. */
function indexOfBare(text: string, needle: string): number {
  let depth = 0;
  let quote = '';
  for (let index = 0; index < text.length; index++) {
    const char = text.charAt(index);
    if (quote !== '') {
      if (char === quote && text.charAt(index - 1) !== '\\') {
        quote = '';
      }
    } else if (char === '"' || char === '\'') {
      quote = char;
    } else if (char === '(' || char === '[') {
      depth++;
    } else if (char === ')' || char === ']') {
      depth--;
    } else if (char === needle && depth === 0) {
      return index;
    }
  }
  return -1;
}

/** Splits a selector list on its own commas, leaving the ones inside `()`, `[]` and quotes alone. */
function splitSelectors(selectors: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let quote = '';
  let start = 0;
  for (let index = 0; index < selectors.length; index++) {
    const char = selectors.charAt(index);
    if (quote !== '') {
      if (char === quote && selectors.charAt(index - 1) !== '\\') {
        quote = '';
      }
    } else if (char === '"' || char === '\'') {
      quote = char;
    } else if (char === '(' || char === '[') {
      depth++;
    } else if (char === ')' || char === ']') {
      depth--;
    } else if (char === ',' && depth === 0) {
      parts.push(selectors.slice(start, index));
      start = index + 1;
    }
  }
  parts.push(selectors.slice(start));
  return parts;
}

/** The document root a drawing's own stylesheet talks about, which after inlining is its wrapper. */
const ROOT = ':root';

/**
 * An id reference in a PRELUDE is a selector; the same text in a declaration can be a colour.
 *
 * `#abc` is both a valid id selector and a valid hex colour, so a rewrite applied to the whole sheet
 * has to guess — and guessing wrong either loses a rule or repaints the drawing. Here there is
 * nothing to guess: this function only ever sees the part before the brace. Hence no boundary
 * gymnastics either, so `.x#id`, `:is(#id, .a)` and `[data-x]#id` are rewritten like any other shape.
 */
function renameIdsInSelector(selector: string, renamedIds: ReadonlyMap<string, string>): string {
  if (renamedIds.size === 0) {
    return selector;
  }
  return selector.replaceAll(/#(?<id>[\w-]+)/gu, (whole, ...rest) => {
    const groups = rest.at(-1) as {id?: string} | undefined;
    const id = groups?.id ?? '';
    const renamed = renamedIds.get(id) ?? null;
    return renamed === null ? whole : `#${renamed}`;
  });
}

function scopeSelectorList(
  selectors: string,
  scope: string,
  onWrapper: string,
  renamedIds: ReadonlyMap<string, string>,
): string {
  const scoped = splitSelectors(selectors)
    .map((selector) => renameIdsInSelector(selector, renamedIds))
    .map((selector) => selector.trim())
    .filter((selector) => selector !== '')
    // A selector that starts with a class the wrapper itself carries is fastened to the wrapper
    // rather than to a descendant of it, because that is where the class lives.
    //
    // `:root` gets the same treatment for a different reason: inside the file it meant "the root of
    // my document", and once the drawing is inlined that root is the wrapper. Left as a descendant
    // it would match nothing at all, and a file that declares its custom properties there — a common
    // way to export one — would lose every one of them without a word.
    .map((selector) => {
      const onRoot = selector === ROOT || selector.startsWith(`${ROOT} `) || selector.startsWith(`${ROOT}:`);
      if (onRoot) {
        return `${scope}${selector.slice(ROOT.length)}`;
      }
      return selector.startsWith(onWrapper) ? `${scope}${selector}` : `${scope} ${selector}`;
    });

  // Nothing recognisable to scope: keep the prelude as it was rather than emit an empty selector,
  // which would swallow the block that follows it.
  return scoped.length === 0 ? selectors : scoped.join(', ');
}

function walk(css: string, scope: string, onWrapper: string, renamedIds: ReadonlyMap<string, string>): string {
  let out = '';
  let index = 0;
  while (index < css.length) {
    const open = css.indexOf('{', index);
    const prelude = css.slice(index, open === -1 ? css.length : open);
    // Only a bare semicolon inside the prelude ends a statement: searched across the whole rest, one
    // in a later string split a rule down the middle, and inside `url("a;b.css")` it splits the URL.
    const bareSemicolon = indexOfBare(prelude, ';');
    const semicolon = bareSemicolon === -1 ? -1 : index + bareSemicolon;
    const isAtRule = prelude.trim().startsWith('@');

    // An at-statement (@import, @charset, @namespace) ends at its semicolon and has no body. Left to
    // the scan below, the rule after it would be read as part of its prelude and ship unscoped.
    const isAtStatement = isAtRule && semicolon !== -1 && (open === -1 || semicolon < open);
    if (isAtStatement) {
      out += css.slice(index, semicolon + 1);
      index = semicolon + 1;
    } else if (open === -1) {
      out += css.slice(index);
      break;
    } else if (isAtRule) {
      const end = blockEnd(css, open);
      // Lower-cased before the lookup: CSS at-rule names are case-insensitive, and `@MEDIA` matched
      // nothing, which shipped its body unscoped — the very leak this branch exists to stop.
      const name = ((/^\s*(?<atRule>@[\w-]+)/u.exec(prelude)?.groups ?? {}).atRule ?? '').toLowerCase();
      if (NESTS_RULES.has(name)) {
        // The body is rules, so it gets the same treatment; only the condition stays untouched.
        const body = css.slice(open + 1, Math.max(open + 1, end - 1));
        out += `${prelude}{${walk(body, scope, onWrapper, renamedIds)}}`;
      } else {
        // @keyframes, @font-face, @property, @counter-style: the body holds no selectors.
        out += prelude + css.slice(open, end);
      }
      index = end;
    } else {
      const end = blockEnd(css, open);
      out += `${scopeSelectorList(prelude, scope, onWrapper, renamedIds)}${css.slice(open, end)}`;
      index = end;
    }
  }
  return out;
}

/**
 * @param css the stylesheet as the drawing wrote it
 * @param scope the wrapper's own selector, e.g. `.assessment-art-k3f9`. Give each element a scope of
 *   its own: a class shared by several drawings confines each stylesheet to all of them, which is the
 *   same leak one step smaller — two cards on screen, and one file's `svg { … }` reaches the other's
 *   drawing.
 * @param onWrapper a class selector the wrapper itself carries, e.g. `.ji-playing`, whose rules
 *   attach to the wrapper instead of to something inside it
 */
export function scopeSvgStyles(
  css: string,
  scope: string,
  onWrapper: string,
  renamedIds: ReadonlyMap<string, string> = new Map(),
): string {
  return walk(stripComments(css), scope, onWrapper, renamedIds);
}
